import { useTranslation } from 'react-i18next';
import { DeliveryAddressMap } from '~/components/common/DeliveryAddressMap';
import { formatAddressText, hasCoordinates } from '~/utils/addressFormat';
import type { Cart as CartType } from '~/graphql/graphql';

type Props = {
  deliveryAddress: NonNullable<CartType['deliveryAddress']>;
  shippingLocationName: string;
  actionsDisabled: boolean;
  onEdit: () => void;
  onClear: () => void;
};

export function SavedDeliveryAddressCard({
  deliveryAddress,
  shippingLocationName,
  actionsDisabled,
  onEdit,
  onClear,
}: Readonly<Props>) {
  const { t } = useTranslation('cart');

  return (
    <div className="p-3 bg-neut-0 rounded-lg border border-prim-300 ring-1 ring-prim-100">
      <p className="text-xs font-semibold text-prim-700 mb-1">
        {t('cart.delivery_address.delivered_here')}
      </p>
      <p className="text-sm text-neut-700" data-testid="delivery-address-text">
        {formatAddressText(deliveryAddress)}
      </p>
      {hasCoordinates(deliveryAddress) && (
        <div className="mt-2">
          <DeliveryAddressMap
            latitude={deliveryAddress.latitude}
            longitude={deliveryAddress.longitude}
          />
        </div>
      )}
      <p className="text-xs text-neut-sec mt-1 italic">
        {t('cart.delivery_address.inventory_note', {
          location: shippingLocationName,
        })}
      </p>
      <div className="flex gap-3 mt-2">
        <button
          type="button"
          className="text-xs font-medium text-prim-700 hover:text-prim-800 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={actionsDisabled}
          onClick={onEdit}
        >
          {t('cart.delivery_address.edit')}
        </button>
        <button
          type="button"
          data-testid="delivery-address-clear"
          className="text-xs text-neut-sec hover:text-neut-prim disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={actionsDisabled}
          onClick={onClear}
        >
          {t('cart.delivery_address.clear')}
        </button>
      </div>
    </div>
  );
}
